import * as React from "react";
import { format } from "date-fns";

import { cn } from "../../lib/utils";
import { Calendar } from "./calendar";
import { Input } from "./input";
import { CalendarIcon } from "../../assets";
import { Button } from "./button";

export function DatePicker({
  id,
  label = "Pick a date",
  className = "",
  date = undefined,
  onDateChange = (date: Date | undefined) => {},
  minDate = undefined,
  maxDate = undefined,
  showLabel = false,
}: {
  id?: string;
  className?: string;
  label?: string;
  date?: Date | undefined;
  onDateChange?: (date: Date | undefined) => void;
  isIcon?: boolean;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
  showLabel?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close calendar when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setIsFocused(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleDateChange = (selectedDate: Date | undefined) => {
    // Prevent deselection (don't allow setting undefined)
    if (!selectedDate) {
      return;
    }

    onDateChange(selectedDate);
    setOpen(false);
    setIsFocused(false);
  };

  const handleInputClick = () => {
    setOpen((prev) => !prev);
    setIsFocused(true);
  };

  return (
    <>
      <div className="flex flex-col">
        {showLabel && label && (
          <span className="BookEZ_font_body_size BookEZ_font_light_bold BookEZ_font_color_gray_2 px-2 py-0.5 rounded">
            {label}
          </span>
        )}
        <div className="relative" ref={containerRef}>
          <Input
            name="calender"
            type="text"
            className={cn(
              `w-full h-10 pr-10 shadow-none min-w-[200px] cursor-pointer transition-all duration-200`,
              isFocused || open
                ? "border-[#2684FF] shadow-[0_0_0_1px_#2684FF]"
                : "",
              className
            )}
            value={date ? format(date, "MM/dd/yyyy") : ""}
            placeholder={label}
            readOnly
            onClick={handleInputClick}
            onFocus={() => setIsFocused(true)}
            onBlur={() => !open && setIsFocused(false)}
          />
          <span
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 cursor-pointer BookEZ_font_color_gray_2"
            onClick={handleInputClick}
          >
            <CalendarIcon
              className="h-4 w-4"
              fill="BookEZ_font_color_gray_2_fill"
            />
          </span>
          {open && (
            <div className="absolute left-0 mt-2 z-50 BookEZ_card_bg border BookEZ_card_border_color BookEZ_card_radius shadow-lg p-2">
              <Calendar
                id={id}
                mode="single"
                selected={date}
                onSelect={handleDateChange}
                initialFocus
                disabled={(day) => {
                  const isBeforeMinDate = minDate ? day < minDate : false;
                  const isAfterMaxDate = maxDate ? day > maxDate : false;
                  return isBeforeMinDate || isAfterMaxDate;
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function IconDatePicker({
  className = "",
  date = undefined,
  onDateChange = (date: Date | undefined) => {},
  minDate = undefined,
  maxDate = undefined,
}: {
  className?: string;
  date?: Date | undefined;
  onDateChange?: (date: Date | undefined) => void;
  minDate?: Date | undefined;
  maxDate?: Date | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setIsFocused(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleDateChange = (selectedDate: Date | undefined) => {
    onDateChange(selectedDate);
    setOpen(false);
    setIsFocused(false);
  };

  const handleButtonClick = () => {
    setOpen((prev) => !prev);
    setIsFocused(true);
  };

  return (
    <div className="relative flex items-center" ref={containerRef}>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "w-10 h-10 p-0 flex items-center justify-center hover:bg-gray-100 rounded-md transition-all duration-200",
          isFocused || open
            ? "ring-2 ring-[#2684FF] ring-offset-0 border-[#2684FF]"
            : "",
          className
        )}
        onClick={handleButtonClick}
        onFocus={() => setIsFocused(true)}
        onBlur={() => !open && setIsFocused(false)}
      >
        <CalendarIcon className="h-4 w-4" />
      </Button>
      {open && (
        <div
          className="absolute z-50 bg-white rounded-lg shadow-lg p-2 border border-[#2684FF]"
          style={{
            top: "45px",
            right: "0px",
          }}
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateChange}
            initialFocus
            disabled={(day) => {
              const isBeforeMinDate = minDate ? day < minDate : false;
              const isAfterMaxDate = maxDate ? day > maxDate : false;
              return isBeforeMinDate || isAfterMaxDate;
            }}
          />
        </div>
      )}
    </div>
  );
}
